/**
 * Utilidades para procesamiento de partes extraídas por IA
 */

import type {
  ArchivoTramite,
  ParteExtraida,
} from '../types/interfaces/GestionPartesIA.interface';

/**
 * Extrae la extensión de un archivo desde su nombre o URL
 */
export const extraerExtensionArchivo = (nombreArchivo: string): string => {
  if (!nombreArchivo) return '';
  const partes = nombreArchivo.split('.');
  return partes.length > 1 ? partes[partes.length - 1].toLowerCase() : '';
};

/**
 * Filtra archivos por extensión
 */
export const filtrarArchivosPorExtension = (
  archivos: ArchivoTramite[],
  extension: string = 'pdf'
): ArchivoTramite[] => {
  if (!Array.isArray(archivos) || archivos.length === 0) return [];

  return archivos.filter(archivo =>
    extraerExtensionArchivo(archivo.URL) === extension.toLowerCase()
  );
};

/**
 * Normaliza texto eliminando caracteres especiales y aplicando transformaciones
 */
export const normalizarTexto = (
  texto: string,
  opciones: {
    /** Lista de caracteres a eliminar del texto */
    caracteresAEliminar?: string[];
    /** Convertir a mayúsculas, minúsculas o sin cambio */
    convertir?: 'may' | 'min' | 'n';
    /** Eliminar tildes */
    eliminarTildes?: boolean;
    /** Eliminar espacios en blanco al inicio y final */
    eliminarEspacios?: boolean;
  } = {}
): string => {
  const {
    caracteresAEliminar = [],
    convertir = 'n',
    eliminarTildes = false,
    eliminarEspacios = false,
  } = opciones;
  if (!texto) return '';

  let resultado = eliminarEspacios ? texto.trim() : texto;

  // Eliminar caracteres especificados
  if (caracteresAEliminar.length > 0) {
    caracteresAEliminar.forEach(char => {
      resultado = resultado.replace(new RegExp(`\\${char}`, 'g'), '');
    });
  }

  // Aplicar transformación de case
  if (convertir === 'may') {
    resultado = resultado.toUpperCase();
  } else if (convertir === 'min') {
    resultado = resultado.toLowerCase();
  }

  // Eliminar tildes
  if (eliminarTildes) {
    resultado = resultado.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  return resultado;
};

/**
 * Normaliza número de teléfono
 */
export const normalizarTelefono = (telefono: string, options: { withFormat?: boolean } = { withFormat: true }): string => {
  if (!telefono) return '';

  // Eliminar todos los caracteres excepto números
  const resultado = telefono.replace(/\D/g, '');

  // Formatear teléfono dominicano (809/829/849) 000-0000
  if (resultado.length === 10 && options.withFormat) {
    const codigo = resultado.substring(0, 3);
    const parte1 = resultado.substring(3, 6);
    const parte2 = resultado.substring(6, 10);
    return `(${codigo}) ${parte1}-${parte2}`;
  } 

  return resultado; 
};

/**
 * Valida si un correo electrónico es válido
 */
export const esCorreoValido = (correo: string): boolean => {
  if (!correo) return false;

  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(correo.trim());
};

/**
 * Capitaliza texto (primera letra de cada palabra en mayúscula)
 */
export const capitalizarTexto = (texto: string): string => {
  if (!texto) return '';

  return texto
    .toLowerCase()
    .split(' ')
    .map(palabra => palabra.charAt(0).toUpperCase() + palabra.slice(1))
    .join(' ');
};

/**
 * Mapea tipo de identificación desde string de IA a ID numérico
 */
export const mapearTipoIdentificacion = (tipoIA: string): number => {
  const mapa: Record<string, number> = {
    'cédula': 1,
    'cedula': 1,
    'pasaporte': 2,
    'rnc': 3,
    'sin documento': 4,
    'sin documentos': 4,
    'matrícula abogado': 5,
    'matricula abogado': 5,
    'acta de nacimiento': 6,
    'acta nacimiento': 6,
    'colegiatura codia': 7,
    'codia': 7,
    'sin rnc': 8,
  };

  const tipoNormalizado = tipoIA?.toLowerCase().trim() || '';
  return mapa[tipoNormalizado] || 7; // Por defecto: Sin Documento
};

/**
 * Mapea tipo de representante desde string de IA a ID numérico
 */
export const mapearTipoRepresentante = (tipoIA: string): number => {
  const mapa: Record<string, number> = {
    'abogado': 1,
    'defensor público': 2,
    'defensor publico': 2,
    'representante físico': 3,
    'representante fisico': 3,
    'apoderado especial': 4,
    'apoderado': 4,
    'agrimensor': 5,
    'tutor': 6,
    'representante del menor': 7,
    'representante del menor y testigo': 8,
    'asesor trabajador': 9,
    'asesor verificador': 10,
    'asesor conciliador': 11,
    'asesor acreedor': 12,
    'asesor liquidador': 13,
    'abogado del estado': 14
  };

  const tipoNormalizado = tipoIA?.toLowerCase().trim() || '';
  return mapa[tipoNormalizado] || 1; // Por defecto: Abogado
};

/**
 * Mapea calidad de involucrado desde string de IA a ID numérico
 */
export const mapearCalidadInvolucrado = (calidadIA: string): number => {
  const mapa: Record<string, number> = {
    'demandante': 1,
    'demandado': 2,
    'tercero': 3,
    'interviniente': 4,
    'recurrente': 5,
    'recurrido': 6,
    'querellante': 7,
    'querellado': 8,
    'actor': 9,
    'ejecutante': 10,
    'ejecutado': 11,
    'imputado': 12,
    'víctima': 13,
    'victima': 13,
    'testigo': 14,
    'perito': 15
  };

  const calidadNormalizada = calidadIA?.toLowerCase().trim() || '';
  return mapa[calidadNormalizada] || 0; // 0 = Sin calidad
};

/**
 * Mapea sexo desde string de IA a ID numérico
 */
export const mapearSexo = (sexoIA: string): number => {
  const sexoNormalizado = sexoIA?.toLowerCase().trim() || '';

  if (sexoNormalizado.includes('masculino') || sexoNormalizado === 'm') {
    return 1;
  } else if (sexoNormalizado.includes('femenino') || sexoNormalizado === 'f') {
    return 2;
  }

  return 0; // Sin especificar
};

/**
 * Formatea fecha para mostrar
 */
export const formatearFecha = (fecha: string | Date): string => {
  if (!fecha) return '';

  const date = typeof fecha === 'string' ? new Date(fecha) : fecha;

  return date.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Trunca texto largo
 */
export const truncarTexto = (texto: string, maxLength: number = 50): string => {
  if (!texto || texto.length <= maxLength) return texto;

  return texto.substring(0, maxLength) + '...';
};

/**
 * Valida si un parte extraído está completo según las reglas de negocio
 * 
 * Reglas de validación:
 * - SIEMPRE requeridos: IdCalidad (≠ 0), Nombre, ParteInvolucrada
 * - Apellidos: requerido si IdTipoIdentificacion ≠ {3, 8}
 * - Identificacion: requerida si IdTipoIdentificacion ≠ {4, 8}
 * 
 * @param parte - Parte a validar
 * @returns Objeto con isValid (boolean) e invalidReason (string opcional)
 * 
 * @example
 * const validation = isParteCompleta(parte);
 * if (!validation.isValid) {
 *   console.log(`Parte inválida: ${validation.invalidReason}`);
 * }
 */
export const isParteCompleta = (
  parte: ParteExtraida
): { isValid: boolean; invalidReason?: string } => {
  // Validaciones siempre requeridas
  if (!parte.IdCalidad || parte.IdCalidad === 0) {
    return {
      isValid: false,
      invalidReason: 'Calidad del parte no está presente',
    };
  }

  if (!parte.Nombre || parte.Nombre.trim() === '') {
    return {
      isValid: false,
      invalidReason: 'Nombre del involucrado es requerido',
    };
  }

  // if (!parte.ParteInvolucrada || parte.ParteInvolucrada.trim() === '') {
  //   return {
  //     isValid: false,
  //     invalidReason: 'Tipo de parte es requerido',
  //   };
  // }

  // Validaciones condicionales para Apellidos
  // Requerido si IdTipoIdentificacion NO es 3 o 8
  const requiereApellidos =
    parte.IdTipoIdentificacion !== 3 && parte.IdTipoIdentificacion !== 8;
  if (requiereApellidos && (!parte.Apellidos || parte.Apellidos.trim() === '')) {
    return {
      isValid: false,
      invalidReason: 'Apellidos es requerido para este tipo de identificación',
    };
  }

  // Validaciones condicionales para Identificacion
  // Requerida si IdTipoIdentificacion NO es 4 u 8
  const requiereIdentificacion =
    parte.IdTipoIdentificacion !== 4 && parte.IdTipoIdentificacion !== 8;
  if (
    requiereIdentificacion &&
    (!parte.Identificacion || parte.Identificacion.trim() === '')
  ) {
    return {
      isValid: false,
      invalidReason:
        'Identificación es requerida para este tipo de identificación',
    };
  }

  return { isValid: true };
};
